import { useState } from 'react';
import { pctEs, numEs } from '../../lib/utils';
import { useNavigate, Link } from 'react-router-dom';
import { Droplets, Wind, Eye, Calendar, Users, CloudRain, Car, Thermometer, FileText, ArrowRight, Star, PartyPopper } from 'lucide-react';
import FreshnessPill from '../FreshnessPill';
import { PENDIENTE as FESTIVO_PENDIENTE } from '../../lib/festivos';
import { freshnessOf, parseTimestamp, madridDate, dayTime } from '../../lib/freshness';
import { pickUv, riverLevel, embalsePct, OBSERVATION_FALLBACK_MAX_AGE_S, windFrom, rainPeriods } from '../../lib/meteo';

// Torrelavega's clock, not the device's: a phone set to another zone should still say
// "Buenos días" in the Torrelavega morning, and show today's date there.
const madridNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));

const getGreeting = () => {
  const hour = madridNow().getHours();
  if (hour < 6) return 'Buenas noches'; // 02:00 is still night in Spain, not "Buenos días"
  if (hour < 12) return 'Buenos días';
  if (hour < 20) return 'Buenas tardes';
  return 'Buenas noches';
};

const getDateString = () => {
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const now = madridNow();
  return `${days[now.getDay()]}, ${now.getDate()} de ${months[now.getMonth()]}`;
};

export function ModernHero({ weather, airQuality, pollen, riverLevels, embalses, stats, user, freshness, eraLive, weatherEnhanced, weatherObservation, newBoletinesCount, todayEventsCount, onShowAuth, forecast, uvRealtime, ayudasAbiertas, ayudasInCard = false, karmaTotals, nextFestivo }) {
  const navigate = useNavigate();
  const greeting = getGreeting();
  const dateStr = getDateString();
  
  // Air quality
  const airData = airQuality?.torrelavega?.[0];
  const airStatus = airData?.ica || null;
  const airPollutant = airData?.debido_a || '';
  const isAirGood = airStatus === 'Buena';
  const isAirReasonable = airStatus?.includes?.('Razonablemente');
  const isAirRegular = airStatus === 'Regular';
  
  // Pollen
  const pollenLevel = pollen?.overall?.label || null;
  // «Sin polen» is the lowest level: it showed in the red of «Alto».
  const isPollenLow = ['Sin polen', 'Nulo', 'Muy bajo', 'Bajo'].includes(pollenLevel);
  
  // Rivers: the basin's overall level, not whichever station came first. A failed fetch is
  // «Sin datos», never «Normal».
  const riverFr = freshnessOf(riverLevels?._freshness ?? freshness?.river ?? freshness?.riverLevels);
  const riverUnavailable = riverFr.status === 'unavailable' || riverLevels?.status === 'unavailable';
  const riverLvl = riverLevels && (riverLevels.overall_severity || riverLevels.estaciones?.length || riverUnavailable)
    ? riverLevel(riverUnavailable ? 'desconocido' : riverLevels.overall_severity)
    : null;
  const riverTone = !riverLvl ? '' : riverLvl.key === 'desconocido' ? 'text-slate-500'
    : riverLvl.rank === 0 ? 'text-emerald-600' : riverLvl.rank === 1 ? 'text-amber-600' : riverLvl.rank === 2 ? 'text-orange-600' : 'text-red-600';
  const pollenFr = freshnessOf(pollen?._freshness ?? freshness?.pollen);
  const airFr = freshnessOf(airQuality?._freshness);
  
  // Community data
  const vecinosCount = stats?.vecinos || 0;
  const karmaPoints = Number(karmaTotals?.points) || 0;

  // Humidity — pick current hour's value or max
  const humidadRaw = weather?.humedad;
  let humidity = null;
  if (humidadRaw) {
    if (typeof humidadRaw === 'number') humidity = humidadRaw;
    else if (humidadRaw.maxima) humidity = humidadRaw.maxima;
  }

  const firstName = user?.nombre?.split(' ')[0];

  // Observation: «ahora» only for a current reading. The old server only nulled the temperature
  // when stale and left humidity, wind and pressure beside the word «ahora».
  const obs = weatherObservation;
  const obsFr = freshnessOf(
    obs?._freshness ?? (obs ? { data_time: obs.timestamp, stale: obs._stale === true ? true : undefined, max_age: OBSERVATION_FALLBACK_MAX_AGE_S } : null),
  );
  const obsTime = parseTimestamp(obs?.timestamp);
  const observedTemp = obsFr.fresh ? obs?.temperatura ?? null : null;
  // Weather icon — from the observation, and only a current one (not this morning's shower)
  const isObservedRain = obsFr.fresh && obs?.precipitacion > 0;
  const hourly = weatherEnhanced?.hourly || [];
  const now = new Date();
  // AEMET hourly data uses Spain dates/hours — convert to Europe/Madrid for matching
  const spainNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
  const todayLocal = `${spainNow.getFullYear()}-${String(spainNow.getMonth() + 1).padStart(2, '0')}-${String(spainNow.getDate()).padStart(2, '0')}`;
  const forecastHourData = hourly.find(h =>
    h.fecha === todayLocal && h.hora === spainNow.getHours()
  );
  const forecastTemp = forecastHourData?.temp;
  const currentTemp = observedTemp ?? forecastTemp;
  const tempSource = observedTemp != null ? 'observado' : forecastTemp != null ? 'previsto' : null;

  // UV: «ahora» only under 45 minutes, else AEMET's maximum for the day ahead
  const today = madridDate();
  const uv = pickUv({
    realtime: uvRealtime,
    aemetMaxFor: (date) => {
      if (date === today && weatherEnhanced?.uvi != null && weatherEnhanced?.uvi_date === today) return weatherEnhanced.uvi;
      return (forecast || []).find(d => (d.fecha || '').slice(0, 10) === date)?.uv_max ?? null;
    },
  });
  const uvShown = uv && uv.value >= 1 ? uv : null;
  const obsExtras = obsFr.fresh && obs && (obs.humedad != null || obs.viento_velocidad != null || obs.presion != null);
  const embPct = embalsePct(embalses?.embalses?.[0]).pct;

  return (
    <div
      className="relative rounded-2xl sm:rounded-3xl mb-6 bg-gradient-to-br from-teal-100/60 via-slate-50 to-emerald-50/50"
      data-testid="modern-hero"
    >
      {/* Decorative blurs — contained within card bounds */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-teal-100/30 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-48 h-48 rounded-full bg-emerald-100/20 blur-3xl" />
      </div>

      <div className="relative z-10 p-5 sm:p-6 md:p-8 lg:p-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6 md:gap-8">
          {/* Left — Greeting + community pills */}
          <div className="md:col-span-7 md:self-center">
            <p className="text-[11px] sm:text-sm text-slate-400 font-medium mb-2 flex items-center gap-1.5">
              <Calendar size={13} className="text-slate-400 shrink-0" />
              <span className="truncate">{dateStr}</span>
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              {greeting},<br />
              <span className="text-torre-teal">{firstName || 'Torrelavega'}</span>
              {user?.karma != null && (
                <button onClick={() => navigate('/perfil')}
                  className="inline-flex items-center gap-1 ml-2 align-middle px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200/60 text-amber-700 text-xs font-bold hover:bg-amber-100 transition-colors"
                  data-testid="hero-karma-pill">
                  <Star size={12} className="text-amber-500 fill-current" /> {user.karma}
                </button>
              )}
              {user?.login_streak >= 2 && (
                <span className="inline-flex items-center gap-1 ml-1 align-middle px-2 py-1 rounded-full bg-orange-50 border border-orange-200/60 text-orange-700 text-[11px] font-bold"
                  data-testid="hero-streak-pill">
                  🔥 {user.login_streak} día{user.login_streak > 1 ? 's' : ''}
                </span>
              )}
            </h1>
            {/* Karma the city has handed out, beside the head count.
                On its own "947 vecinos" is a registration figure — it says people signed up, not
                that anything happened. The karma total is the same community measured by what it
                did: every point is a pothole reported, a neighbour helped, a profile completed.
                It is the number that turns a directory into evidence, which is why it belongs in
                the first thing somebody reads rather than in an admin panel. Hidden when it is
                zero, because a proud zero is worse than no claim at all. */}
            {vecinosCount > 0 && (
              user ? (
                <div className="px-3 py-1 bg-white/60 border border-slate-200/50 rounded-2xl inline-flex items-center gap-2 mt-3 mb-1 text-left" data-testid="hero-vecinos-count">
                  <span className="text-xs font-medium text-slate-600">
                    <strong className="text-slate-900">{numEs(vecinosCount)}</strong> {vecinosCount === 1 ? 'vecino' : 'vecinos'} en tu ciudad
                    {karmaPoints > 0 && (
                      <> · <strong className="text-slate-900">{numEs(karmaPoints)}</strong> {karmaPoints === 1 ? 'punto' : 'puntos'} de karma repartidos</>
                    )}
                  </span>
                </div>
              ) : (
                // rounded-2xl, not rounded-full: the browser caps a radius at half the height, so on
                // one line this is still a full pill, but when large text wraps it onto two or three
                // lines it becomes a rounded rectangle instead of a stretched oval with the words
                // crammed into it. Even padding: pl-1 was left over from an avatar stack that no
                // longer renders (its empty <div> went too), and made the text hug the left edge.
                <button onClick={onShowAuth}
                  className="px-3 py-1 bg-white/80 border border-slate-200 rounded-2xl inline-flex items-center gap-2 hover:bg-white active:scale-95 transition-all group shadow-sm mt-3 mb-1 text-left"
                  aria-label="Únete a la comunidad de vecinos de Torrelavega"
                  data-testid="hero-vecinos-cta">
                  <div className="flex items-center gap-x-1.5 gap-y-0.5 flex-wrap">
                    <span className="text-xs font-semibold text-slate-600">
                      <strong className="text-slate-900">{numEs(vecinosCount)}</strong> {vecinosCount === 1 ? 'vecino' : 'vecinos'}
                      {karmaPoints > 0 && <> · <strong className="text-slate-900">{numEs(karmaPoints)}</strong> {karmaPoints === 1 ? 'punto' : 'puntos'} de karma</>}.
                    </span>
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-0.5">Únete <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" /></span>
                  </div>
                </button>
              )
            )}

            {/* Community pills */}
            <div className="flex flex-wrap gap-2 mt-4 sm:mt-6 relative">
              {/* The incidencias and resueltas pills went (28 Sep 2026): Tu ciudad, right below, shows
                  the same two counts in its Incidencias card, with the latest reports. */}
              {/* Only when Tu ciudad has no Ayuda vecinal card to carry the count (the viewer's own
                  help activity replaces it). */}
              {ayudasAbiertas > 0 && !ayudasInCard && (
                <button
                  onClick={() => navigate('/vecinos')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-200/60 text-xs font-bold text-indigo-700 hover:bg-indigo-100 transition-colors"
                  data-testid="hero-ayudas-pill"
                >
                  <Users size={12} />
                  {ayudasAbiertas} ayuda{ayudasAbiertas > 1 ? 's' : ''}
                </button>
              )}
              {todayEventsCount > 0 && (
                <button
                  onClick={() => navigate('/agenda')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200/60 text-xs font-bold text-purple-700 hover:bg-purple-100 transition-colors"
                  data-testid="hero-eventos-pill"
                >
                  <Calendar size={12} />
                  {todayEventsCount} evento{todayEventsCount > 1 ? 's' : ''} hoy
                </button>
              )}
              {newBoletinesCount > 0 && (
                <button
                  onClick={() => navigate('/boletines')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-200/60 text-xs font-bold text-indigo-700 hover:bg-indigo-100 transition-colors"
                  data-testid="hero-boletines-pill"
                >
                  <FileText size={12} />
                  {newBoletinesCount} {newBoletinesCount > 1 ? 'boletines' : 'boletín'} hoy
                </button>
              )}
              {/* Próximo festivo, as a pill rather than the 71px banner it used to be.
                  That banner sat at 3.916px on a 3.817px page — past the sign-up ask, past the
                  feedback link, below the footer fold — to tell somebody a bank holiday is in
                  twenty days. It is one fact, the same shape as every other fact in this row, and
                  here it is read by people who never scrolled that far. */}
              {nextFestivo && (
                <button
                  onClick={() => navigate('/festivos')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl text-left bg-amber-50 border border-amber-200/60 text-xs font-bold text-amber-700 hover:bg-amber-100 transition-colors"
                  data-testid="hero-festivo-pill"
                >
                  <PartyPopper size={12} />
                  {nextFestivo.is_today_festivo
                    ? `Hoy es festivo · ${nextFestivo.name}`
                    : `${nextFestivo.name} en ${nextFestivo.days_until} día${nextFestivo.days_until === 1 ? '' : 's'}`}
                  {/* A local day whose year is not yet in the BOC is not official yet. */}
                  {nextFestivo.pendiente ? ` · ${FESTIVO_PENDIENTE}` : ''}
                </button>
              )}
            </div>
          </div>

          {/* Right — Weather + status indicators in a white card */}
          <div className="md:col-span-5">
            {(weather || airStatus || pollenLevel || riverLvl || eraLive) ? (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/60 p-4 sm:p-5 shadow-sm">
              {/* Temperature — clickable to observed data on Tiempo page */}
              {weather && (
                <Link to="/tiempo#observation" className="flex items-start gap-3 mb-3 hover:opacity-80 transition-opacity cursor-pointer min-w-0" data-testid="hero-temp-link">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-1.5">
                      <span className="text-4xl sm:text-5xl font-extrabold text-slate-900 tabular-nums leading-none">
                        {currentTemp != null ? numEs(currentTemp) : (weather?.temperatura?.maxima ?? weather?.temperatura ?? '--')}°
                      </span>
                      {currentTemp != null ? (
                        <span className="text-sm text-slate-500 font-semibold self-end mb-1">
                          {tempSource === 'observado' ? 'ahora' : 'previsto'}
                        </span>
                      ) : (
                        <span className="text-xl sm:text-2xl text-slate-300 font-bold">
                          /{weather?.temperatura?.minima || weather?.temp_min || '--'}°
                        </span>
                      )}
                    </div>
                    {currentTemp != null && (
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        máx {weather?.temperatura?.maxima ?? '--'}° · mín {weather?.temperatura?.minima ?? weather?.temp_min ?? '--'}° <span className="text-[11px] text-slate-400">(prev.)</span>
                      </p>
                    )}
                    {/* Observed conditions from AEMET station — only while current */}
                    {(obsExtras || uvShown) && (
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        {obsExtras && obs.humedad != null && (
                          <span className="text-[11px] text-slate-500 font-bold flex items-center gap-0.5">
                            <Droplets size={10} aria-hidden="true" /> {numEs(obs.humedad, 0)}%
                          </span>
                        )}
                        {obsExtras && obs.viento_velocidad != null && (
                          <span className="text-[11px] text-cyan-700 font-bold flex items-center gap-0.5">
                            <Wind size={10} aria-hidden="true" /> {numEs(obs.viento_velocidad, 0)} km/h {windFrom(obs.viento_direccion)}
                          </span>
                        )}
                        {obsExtras && obs.presion != null && (
                          <span className="text-[11px] text-slate-400 font-medium">
                            {numEs(obs.presion)} hPa
                          </span>
                        )}
                        {uvShown && (
                          <span className={`text-[11px] font-bold flex items-center gap-0.5 px-1.5 py-0.5 rounded-full ${
                            uvShown.value >= 8 ? 'bg-red-50 text-red-600' :
                            uvShown.value >= 6 ? 'bg-orange-50 text-orange-600' :
                            uvShown.value >= 3 ? 'bg-amber-50 text-amber-600' :
                            'bg-emerald-50 text-emerald-600'
                          }`} data-testid="hero-uv">
                            <Eye size={9} aria-hidden="true" /> {uvShown.mode === 'now' ? `UV ${uvShown.value} ahora` : uvShown.mode === 'max_tomorrow' ? `UV máx ${uvShown.value} mañana` : `UV máx ${uvShown.value} hoy`}
                          </span>
                        )}
                      </div>
                    )}
                    {obs?.station && (
                      <p className="text-[11px] text-slate-400 mt-1 flex flex-wrap items-center gap-1">
                        <span>{obs.station}{obsTime != null ? ` · ${dayTime(obsTime)}` : ''}</span>
                        <FreshnessPill info={obsFr} testId="hero-observation-stale" />
                      </p>
                    )}
                    <p className="text-xs sm:text-sm text-slate-400 mt-1.5 flex items-center gap-1.5 flex-wrap">
                      {weather?.descripcion || weather?.estado_cielo || 'Cargando...'}
                      <FreshnessPill info={freshnessOf(weather?._freshness ?? freshness?.weather)} testId="weather-stale" />
                    </p>
                    {/* Feels like — only show when differs by 3°+ from actual */}
                    {weather?.sens_termica?.maxima && Math.abs(parseInt(weather?.sens_termica.maxima) - parseInt(weather?.temperatura?.maxima || 0)) >= 3 && (
                      <p className="text-[11px] text-orange-600 font-bold mt-1">🌡️ Sensación {weather?.sens_termica.maxima}°</p>
                    )}
                    {/* 6h rain periods — show most granular level only */}
                    {weather?.rain_periods?.length > 0 && weather?.rain_periods.some(r => r.prob > 0) && (() => {
                      const periods = rainPeriods(weather?.rain_periods);
                      return (
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                          {periods.map((r, i) => (
                            <span key={i} className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${r.prob >= 70 ? 'bg-blue-100 text-blue-700' : r.prob >= 30 ? 'bg-blue-50 text-blue-500' : 'bg-slate-50 text-slate-400'}`}>
                              {r.label} {r.prob}%
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                    {/* Daily temperature progression */}
                    {weather?.temp_curve && Object.keys(weather?.temp_curve).length >= 3 && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                        {[{k:'h06',l:'Mañana'},{k:'h12',l:'Mediodía'},{k:'h18',l:'Tarde'},{k:'h24',l:'Noche'}].map(({k,l}) => {
                          const v = weather?.temp_curve[k];
                          if (v == null) return null;
                          const color = v >= 35 ? 'text-red-600' : v >= 28 ? 'text-orange-600' : v >= 20 ? 'text-slate-700' : 'text-blue-600';
                          return (
                            <div key={k} className="text-center">
                              <p className={`text-sm font-extrabold ${color}`}>{v}°</p>
                              <p className="text-[11px] text-slate-400">{l}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Weather icon — based on observed data */}
                  <div className="ml-auto shrink-0 hidden min-[400px]:block">
                    {isObservedRain ? <CloudRain size={28} className="text-torre-teal" /> : 
                     <Thermometer size={28} className="text-slate-300" />}
                  </div>
                </Link>
              )}

              {/* 2-day forecast strip — skip today, show next 2 days */}
              {forecast?.length >= 2 && (() => {
                const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
                const futureDays = forecast.filter(d => (d.fecha || '').slice(0, 10) > today).slice(0, 2);
                return futureDays.length > 0 ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-2 border-t border-slate-100 text-xs">
                  {futureDays.map((d, i) => (
                    <Link key={i} to="/tiempo" className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 transition-colors">
                      <span className="font-bold text-slate-600">{d.dia_semana?.slice(0, 3)}</span>
                      <span className="font-extrabold text-slate-800">{d.temperatura?.maxima}°</span>
                      <span className="text-slate-400">{d.temperatura?.minima}°</span>
                      {d.prob_precipitacion > 20 && <Droplets size={10} className="text-torre-teal" />}
                    </Link>
                  ))}
                  <span className="text-[11px] text-slate-400">(prev.)</span>
                </div>
                ) : null;
              })()}

              {/* Status indicators — compact inline list */}
              <div className="space-y-1.5 pt-2 border-t border-slate-100">
                {airStatus && (
                  <Link to="/tiempo#air-quality" className="flex items-center gap-2 text-xs hover:bg-slate-50 px-1 py-0.5 rounded-md transition-colors cursor-pointer" data-testid="hero-air-quality">
                    <Wind size={12} className={`shrink-0 ${isAirGood ? 'text-emerald-500' : isAirReasonable ? 'text-torre-teal' : isAirRegular ? 'text-amber-500' : 'text-red-500'}`} />
                    <span className={`font-semibold ${isAirGood ? 'text-emerald-600' : isAirReasonable ? 'text-torre-teal' : isAirRegular ? 'text-amber-600' : 'text-red-600'}`}>
                      C. Aire: {airStatus.charAt(0).toUpperCase() + airStatus.slice(1).toLowerCase()}
                      {airPollutant && <span className="text-slate-400 font-normal"> ({airPollutant})</span>}
                    </span>
                    <FreshnessPill info={airFr} testId="air-stale" />
                  </Link>
                )}
                {pollenLevel && (
                  <Link to="/tiempo#pollen" className="flex items-center gap-2 text-xs hover:bg-slate-50 px-1 py-0.5 rounded-md transition-colors cursor-pointer" data-testid="hero-pollen">
                    <span className={`w-3 h-3 rounded-full shrink-0 ${isPollenLow ? 'bg-emerald-400' : pollenLevel === 'Moderado' ? 'bg-amber-400' : 'bg-red-400'}`} />
                    <span className={`font-semibold ${isPollenLow ? 'text-emerald-600' : pollenLevel === 'Moderado' ? 'text-amber-600' : 'text-red-600'}`}>
                      Polen: {pollenLevel.charAt(0).toUpperCase() + pollenLevel.slice(1).toLowerCase()}
                    </span>
                    <FreshnessPill info={pollenFr} testId="pollen-stale" />
                  </Link>
                )}
                {riverLvl && (
                  <Link to="/tiempo#rivers" className="flex items-center gap-2 text-xs hover:bg-slate-50 px-1 py-0.5 rounded-md transition-colors cursor-pointer" data-testid="hero-rivers">
                    <Eye size={12} className={`shrink-0 ${riverTone}`} aria-hidden="true" />
                    <span className={`font-semibold ${riverTone}`}>
                      Ríos: {riverLvl.label}
                    </span>
                    <FreshnessPill info={riverFr} testId="rivers-stale" />
                  </Link>
                )}
                {/* Embalses — Alsa-Mediajo (reserva hídrica de la comarca) */}
                {embalses?.embalses?.[0] && embPct != null && (
                  <Link to="/tiempo#embalses" className="flex items-center gap-2 text-xs hover:bg-slate-50 px-1 py-0.5 rounded-md transition-colors cursor-pointer" data-testid="hero-embalses">
                    <Droplets size={12} className="shrink-0 text-torre-teal" />
                    <span className="font-semibold text-torre-teal">
                      Embalse Alsa: {pctEs(embPct, 1)}
                    </span>
                    {embalses.embalses[0].volumen != null && embalses.embalses[0].capacidad != null && (
                      <span className="text-[11px] text-slate-400">{numEs(embalses.embalses[0].volumen, 2)}/{numEs(embalses.embalses[0].capacidad, 2)} hm³</span>
                    )}
                  </Link>
                )}
                {/* ERA Parking live — «en vivo» only while Parkunload's count is current (10 min);
                    the line used to say it of any snapshot, however old. */}
                {eraLive && eraLive.total_capacity > 0 && freshnessOf(eraLive._freshness).fresh && (
                  <Link to="/aparcamiento" className="flex items-center gap-2 text-xs" data-testid="hero-era-live">
                    <Car size={12} className={`shrink-0 ${eraLive.pct_occupied > 80 ? 'text-red-500' : eraLive.pct_occupied > 50 ? 'text-amber-500' : 'text-emerald-500'}`} />
                    <span className={`font-semibold ${eraLive.pct_occupied > 80 ? 'text-red-600' : eraLive.pct_occupied > 50 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      ERA: {eraLive.total_available}/{eraLive.total_capacity} libres
                    </span>
                    <span className="text-[11px] text-slate-400">en vivo</span>
                  </Link>
                )}
                {/* Beach pill — day: today's conditions, night: tomorrow's */}
                {(() => {
                  const hourly = weatherEnhanced?.hourly || [];
                  const spainHour = parseInt(new Date().toLocaleTimeString('en-GB', { timeZone: 'Europe/Madrid', hour: '2-digit' }));
                  const isNight = spainHour >= 21 || spainHour < 6;
                  const targetDate = new Date();
                  if (isNight && spainHour >= 21) targetDate.setDate(targetDate.getDate() + 1);
                  const target = targetDate.toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
                  const targetHours = hourly.filter(h => h.fecha === target && parseInt(h.hora) >= 10 && parseInt(h.hora) <= 18);
                  const maxTemp = targetHours.length ? Math.max(...targetHours.map(h => h.temp)) : null;
                  const maxRain = targetHours.length ? Math.max(...targetHours.map(h => h.precip_prob || 0)) : null;
                  const maxWind = targetHours.length ? Math.max(...targetHours.map(h => h.wind_speed || 0)) : null;
                  const hasBadSky = targetHours.some(h => /lluvia|tormenta/i.test(h.sky_desc || ''));
                  const isGreat = maxTemp >= 22 && maxRain <= 10 && maxWind <= 20 && !hasBadSky;
                  const isOk = maxTemp >= 19 && maxRain <= 20 && maxWind <= 30 && !hasBadSky;
                  const prefix = isNight ? 'Mañana: ' : '';
                  const label = isGreat ? `${prefix}Día de playa` : isOk ? `${prefix}Playa posible` : 'Ver playas';
                  const emoji = isGreat ? '🏖️' : isOk ? '⛅' : '🌊';
                  const color = isGreat ? 'text-torre-teal' : isOk ? 'text-amber-600' : 'text-slate-500';
                  const tempStr = maxTemp != null ? ` · ${maxTemp}°` : '';
                  return (
                    <Link to="/playas" className="flex items-center gap-2 text-xs hover:bg-torre-teal/5 px-1 py-0.5 rounded-md transition-colors cursor-pointer" data-testid="hero-beach-pill">
                      <span className="text-sm shrink-0">{emoji}</span>
                      <span className={`font-semibold ${color}`}>{label}{tempStr}</span>
                      <ArrowRight size={10} className={color} />
                    </Link>
                  );
                })()}
                {/* Source attribution — single line */}
                {(airStatus || pollenLevel || riverLvl) && (
                  <p className="text-[11px] text-slate-400 pt-1">
                    {[airStatus && 'Gob. Cantabria', pollenLevel && 'Open-Meteo', riverLvl && 'SAIH Cantábrico'].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
            </div>
            ) : null}

          </div>
        </div>
      </div>
    </div>
  );
}
